import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Crown, Star, Rocket, Sparkles, Shield } from 'lucide-react';
import { authAPI, resumeAPI, visualResumeAPI, coverLetterAPI, jobAPI, savedCareersAPI, careerPlannerAPI, assessmentAPI, settingsAPI, authenticatedFetch } from '@/lib/api';
import { toast } from 'sonner';
import WelcomeTour, { useShouldShowTour } from '@/components/WelcomeTour';
import SkipNav from '@/components/SkipNav';
import ConfirmDialog from '@/components/ConfirmDialog';
import AppFooter from '@/components/AppFooter';
import AdRenderer from '@/components/AdRenderer';
import { useAdTriggerContext } from '@/contexts/AdTriggerContext';
import { FounderCountdownBanner } from '@/components/launch/FounderCountdownBanner';
import useLaunchStatus from '@/hooks/useLaunchStatus';
import { Badge } from '@/components/ui/badge';
import LoggedInHeader from '@/components/navigation/LoggedInHeader';

// Simple in-memory cache to prevent re-fetching on back navigation
const dashboardCache = {
  data: null,
  settings: null,
  timestamp: null,
  // Cache expires after 60 seconds (increased for performance)
  isValid: function() {
    return this.data && this.timestamp && (Date.now() - this.timestamp < 60000);
  }
};

// GPS Onboarding Components
import GPSOnboarding, { useGPSOnboarding } from '@/components/GPSOnboarding';
import GPSStartHereCard from '@/components/dashboard/GPSStartHereCard';
import PostStepEncouragement from '@/components/dashboard/PostStepEncouragement';
import GPSResponsesViewer from '@/components/GPSResponsesViewer';

// Founder Trial Badge
import { FounderTrialBadge } from '@/components/dashboard/FounderTrialBadge';

// Admin Widget: Founders Expiring Soon
import FoundersExpiringSoonWidget from '@/components/dashboard/FoundersExpiringSoonWidget';

// Partner Onboarding Component
import PartnerOnboarding from '@/components/PartnerOnboarding';

// Core Dashboard Components (loaded immediately)
import {
  StatsGrid,
  QuickActionsGrid,
  DashboardNav,
  AffiliateBanner,
  ResumeOptionsDialog,
  RecommendedAction,
  OrgPremiumAccessBanner,
  PremiumExpiredModal,
  ComingSoonSection
} from '@/components/dashboard';

// Lazy-loaded Dashboard Sections (loaded on demand for better initial load)
const ResumesSection = lazy(() => import('@/components/dashboard/ResumesSection'));
const CoverLettersSection = lazy(() => import('@/components/dashboard/CoverLettersSection'));
const CareerPlansSection = lazy(() => import('@/components/dashboard/CareerPlansSection'));
const AssessmentsSection = lazy(() => import('@/components/dashboard/AssessmentsSection'));
const SavedCareersSection = lazy(() => import('@/components/dashboard/SavedCareersSection'));
const MyRegisteredEvents = lazy(() => import('@/components/dashboard/MyRegisteredEvents'));
const MyPurchasedServicesSection = lazy(() => import('@/components/dashboard/MyPurchasedServicesSection'));

// Section Loading Skeleton
const SectionSkeleton = () => (
  <div className="bg-white rounded-lg p-6 border border-gray-100 animate-pulse">
    <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
    <div className="space-y-3">
      <div className="h-20 bg-gray-100 rounded"></div>
      <div className="h-20 bg-gray-100 rounded"></div>
    </div>
  </div>
);

// Dashboard Skeleton Loader
import { DashboardSkeleton } from '@/components/dashboard/DashboardSkeleton';

// Account Type Badge Component
const AccountTypeBadge = ({ user }) => {
  if (!user) return null;
  
  // Determine account type and styling
  let label, bgColor, textColor, Icon;
  
  if (user.is_vip) {
    label = 'VIP';
    bgColor = 'bg-purple-100';
    textColor = 'text-purple-700';
    Icon = Crown;
  } else if (user.is_founder) {
    label = 'Founder Circle';
    bgColor = 'bg-amber-100';
    textColor = 'text-amber-700';
    Icon = Star;
  } else if (user.is_early_adopter) {
    label = 'Early Adopter';
    bgColor = 'bg-blue-100';
    textColor = 'text-blue-700';
    Icon = Rocket;
  } else if (user.is_standard_premium || ['premium_monthly', 'premium_yearly', 'premium'].includes(user.subscription_tier)) {
    label = 'Standard Premium';
    bgColor = 'bg-indigo-100';
    textColor = 'text-indigo-700';
    Icon = Sparkles;
  } else {
    label = 'Standard Free';
    bgColor = 'bg-gray-100';
    textColor = 'text-gray-600';
    Icon = Shield;
  }
  
  return (
    <Badge className={`${bgColor} ${textColor} flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium`}>
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </Badge>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { triggerLogoutAd } = useAdTriggerContext();
  const [user, setUser] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [showResumeOptionsDialog, setShowResumeOptionsDialog] = useState(false);
  const [coverLetters, setCoverLetters] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [savedCareers, setSavedCareers] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    career_plans: [],
    career_plans_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [userSettings, setUserSettings] = useState(null);
  const [affiliateStatus, setAffiliateStatus] = useState(null);
  const [hasOrgMembership, setHasOrgMembership] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  
  // Access status (fetched once and passed to children)
  const [accessStatus, setAccessStatus] = useState(null);
  
  // User purchases (fetched once from consolidated endpoint)
  const [userPurchases, setUserPurchases] = useState(null);
  
  // Registered events (fetched once from consolidated endpoint)
  const [registeredEvents, setRegisteredEvents] = useState([]);
  
  // GPS data from API (fetched once from consolidated endpoint)
  const [gpsDataFromApi, setGpsDataFromApi] = useState(null);
  
  // GED status (fetched once from consolidated endpoint)
  const [gedStatus, setGedStatus] = useState(null);
  
  // Progress badges (fetched once from consolidated endpoint)
  const [progressBadges, setProgressBadges] = useState({});
  
  // Launch status hook for founder countdown
  const { 
    isFounder, 
    showCountdownBanner, 
    countdownBannerType, 
    founderDaysRemaining, 
    countdownMessage 
  } = useLaunchStatus();
  
  // Welcome tour state
  const shouldShowTour = useShouldShowTour();
  const [showTour, setShowTour] = useState(false);
  
  // GPS Onboarding state
  const { 
    shouldShow: shouldShowGPS, 
    setShouldShow: setShouldShowGPS, 
    gpsData, 
    loading: gpsLoading, 
    resetOnboarding: resetGPSOnboarding,
    checkGPSStatus 
  } = useGPSOnboarding();
  const [showGPSOnboarding, setShowGPSOnboarding] = useState(false);
  const [showGPSResponses, setShowGPSResponses] = useState(false);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {}
  });

  // Helper function to check user roles
  const hasRole = (role) => {
    return user?.roles?.includes(role) || user?.role === role;
  };
  
  // Helper function to check if user is SUPER ADMIN ONLY
  // Only super_admin gets access to admin tools
  const isSuperAdmin = () => {
    return hasRole('super_admin');
  };
  
  // Section ordering state
  const defaultSectionOrder = ['purchasedServices', 'resumes', 'coverLetters', 'careerPlans', 'assessments'];
  const [sectionOrder, setSectionOrder] = useState(() => {
    const saved = localStorage.getItem('dashboardSectionOrder');
    return (saved && saved !== 'undefined') ? JSON.parse(saved) : defaultSectionOrder;
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData && userData !== 'undefined') {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user');
      }
    }
    loadAllData();
    checkAffiliateStatus();
    checkOrgMembership();
  }, []);

  const checkOrgMembership = async () => {
    try {
      const response = await authenticatedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/my-memberships`);
      
      if (response.ok) {
        const data = await response.json();
        setHasOrgMembership(data.memberships && data.memberships.length > 0);
      }
    } catch (error) {
      console.error('Error checking org membership:', error);
      setHasOrgMembership(false);
    }
  };

  const checkAffiliateStatus = async () => {
    try {
      const response = await authenticatedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/affiliates/my-dashboard`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'approved' && !data.agreement_signed) {
          setAffiliateStatus('pending_agreement');
        }
      }
      // Silently ignore 404/401 - user might not be an affiliate
    } catch (error) {
      // Network errors only - don't log expected auth failures
      if (error.name !== 'TypeError') {
        console.error('Error checking affiliate status:', error);
      }
    }
  };

  useEffect(() => {
    const tourShown = localStorage.getItem('tourShown');
    const launchAdvancedTour = localStorage.getItem('launchAdvancedTour');
    
    if (launchAdvancedTour === 'true') {
      localStorage.removeItem('launchAdvancedTour');
      setShowTour(true);
    } else if (shouldShowTour && !loading && !tourShown) {
      const timer = setTimeout(() => {
        setShowTour(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shouldShowTour, loading]);

  // GPS Onboarding trigger - NO LONGER automatic popup
  // Now shows a contextual prompt card instead (see PersonalizePathCard below)
  // Users can start GPS onboarding via:
  // 1. The PersonalizePathCard on the dashboard
  // 2. "Personalize My Path" button in settings
  // 3. Quick Actions section

  const handleGPSComplete = (data) => {
    setShowGPSOnboarding(false);
    setShouldShowGPS(false);
    checkGPSStatus(); // Refresh GPS data
    toast.success('Your personalized path is ready!');
  };

  const handleGPSSkip = () => {
    setShowGPSOnboarding(false);
    setShouldShowGPS(false);
  };

  // Show GPS Responses viewer when "Personalize My Path" is clicked
  const handlePersonalizePath = () => {
    setShowGPSResponses(true);
  };

  // Reset GPS and start onboarding flow
  const handleResetGPS = async () => {
    await resetGPSOnboarding();
    setShowGPSResponses(false);
    setShowGPSOnboarding(true);
  };

  const loadAllData = async (forceRefresh = false) => {
    // Use cached data if available and valid (for back navigation)
    if (!forceRefresh && dashboardCache.isValid()) {
      // Apply cached data immediately - includes ALL consolidated data
      if (dashboardCache.settings) {
        setUserSettings(dashboardCache.settings);
        if (dashboardCache.settings.dashboard_section_order) {
          setSectionOrder(dashboardCache.settings.dashboard_section_order);
        }
      }
      if (dashboardCache.data) {
        setResumes(dashboardCache.data.resumes || []);
        setCoverLetters(dashboardCache.data.coverLetters || []);
        setSavedJobs(dashboardCache.data.savedJobs || []);
        setSavedCareers(dashboardCache.data.savedCareers || []);
        setAssessments(dashboardCache.data.assessments || []);
        setDashboardData(dashboardCache.data.dashboardData || {});
        // Restore additional consolidated data
        if (dashboardCache.data.accessStatus) setAccessStatus(dashboardCache.data.accessStatus);
        if (dashboardCache.data.purchases) setUserPurchases(dashboardCache.data.purchases);
        if (dashboardCache.data.registeredEvents) setRegisteredEvents(dashboardCache.data.registeredEvents);
        if (dashboardCache.data.gpsData) setGpsDataFromApi(dashboardCache.data.gpsData);
        if (dashboardCache.data.gedStatus) setGedStatus(dashboardCache.data.gedStatus);
        if (dashboardCache.data.progressBadges) setProgressBadges(dashboardCache.data.progressBadges);
        setLoading(false);
        return;
      }
    }
    // Settings are now included in super-consolidated, so just fetch data
    await fetchData();
  };

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      const data = response.data;
      setUserSettings(data);
      dashboardCache.settings = data;
      
      // Load section order from settings if available
      if (data.dashboard_section_order && Array.isArray(data.dashboard_section_order)) {
        setSectionOrder(data.dashboard_section_order);
        localStorage.setItem('dashboardSectionOrder', JSON.stringify(data.dashboard_section_order));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // PERFORMANCE OPTIMIZED: Single super-consolidated API call replaces 15+ individual calls
      const response = await fetch(`${API_URL}/api/dashboard/super-consolidated`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        const result = await response.json();
        const data = result.data;
        
        // Core dashboard data
        setResumes(data.resumes || []);
        setCoverLetters(data.cover_letters || []);
        setSavedJobs(data.saved_jobs || []);
        setSavedCareers(data.saved_careers || []);
        setAssessments(data.assessments || []);
        setDashboardData({
          career_plans: data.career_plans || [],
          career_plans_count: data.stats?.career_plans_count || 0
        });
        
        // Settings from consolidated response
        if (data.settings) {
          setUserSettings(data.settings);
          dashboardCache.settings = data.settings;
          if (data.settings.dashboard_section_order && Array.isArray(data.settings.dashboard_section_order)) {
            setSectionOrder(data.settings.dashboard_section_order);
            localStorage.setItem('dashboardSectionOrder', JSON.stringify(data.settings.dashboard_section_order));
          }
        }
        
        // Access status (replaces separate /api/access/status call)
        if (data.access_status) {
          setAccessStatus(data.access_status);
        }
        
        // User purchases (replaces /api/user/purchases/my-services call)
        if (data.purchases) {
          setUserPurchases(data.purchases);
        }
        
        // Registered events (replaces /api/events/user/my-registrations call)
        if (data.registered_events) {
          setRegisteredEvents(data.registered_events);
        }
        
        // GPS data (replaces multiple /api/gps/* calls)
        if (data.gps_data) {
          setGpsDataFromApi(data.gps_data);
        }
        
        // GED status (replaces /api/ged/application call)
        if (data.ged_status) {
          setGedStatus(data.ged_status);
        }
        
        // Progress badges (replaces /api/progress/badges call)
        if (data.progress_badges) {
          setProgressBadges(data.progress_badges);
        }
        
        // Cache ALL the data for faster back navigation
        dashboardCache.data = {
          resumes: data.resumes || [],
          coverLetters: data.cover_letters || [],
          savedJobs: data.saved_jobs || [],
          savedCareers: data.saved_careers || [],
          assessments: data.assessments || [],
          dashboardData: {
            career_plans: data.career_plans || [],
            career_plans_count: data.stats?.career_plans_count || 0
          },
          accessStatus: data.access_status,
          purchases: data.purchases,
          registeredEvents: data.registered_events,
          gpsData: data.gps_data,
          gedStatus: data.ged_status,
          progressBadges: data.progress_badges
        };
        dashboardCache.timestamp = Date.now();
      } else {
        // Fallback to individual API calls if super-consolidated fails
        console.warn('Super-consolidated API failed, using fallback');
        await fetchDataFallback();
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Try fallback on error
      await fetchDataFallback();
    } finally {
      setLoading(false);
    }
  };

  // Fallback to individual API calls (legacy method)
  const fetchDataFallback = async () => {
    try {
      const results = await Promise.allSettled([
        resumeAPI.getAll(),
        visualResumeAPI.getAll(),
        coverLetterAPI.getAll(),
        jobAPI.getSaved(),
        savedCareersAPI.getAll(),
        careerPlannerAPI.getUserPlans(),
        assessmentAPI.getResults()
      ]);

      const failedSources = [];
      
      const resumesRes = results[0].status === 'fulfilled' ? results[0].value : (failedSources.push('resumes'), { data: [] });
      const visualResumesRes = results[1].status === 'fulfilled' ? results[1].value : (failedSources.push('visual resumes'), { data: [] });
      const lettersRes = results[2].status === 'fulfilled' ? results[2].value : (failedSources.push('cover letters'), { data: [] });
      const jobsRes = results[3].status === 'fulfilled' ? results[3].value : (failedSources.push('saved jobs'), { data: [] });
      const careersRes = results[4].status === 'fulfilled' ? results[4].value : (failedSources.push('saved careers'), { data: [] });
      const plansRes = results[5].status === 'fulfilled' ? results[5].value : (failedSources.push('career plans'), { data: [] });
      const assessmentsRes = results[6].status === 'fulfilled' ? results[6].value : (failedSources.push('assessments'), { data: [] });

      if (failedSources.length > 0) {
        console.warn('Some dashboard data sources failed to load:', failedSources);
      }

      const allResumes = [
        ...(Array.isArray(resumesRes.data) ? resumesRes.data : []).map(r => ({ ...r, type: 'standard' })),
        ...(Array.isArray(visualResumesRes.data) ? visualResumesRes.data : []).map(r => ({ ...r, type: 'visual' }))
      ].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

      const coverLettersData = Array.isArray(lettersRes.data) ? lettersRes.data : [];
      const savedJobsData = Array.isArray(jobsRes.data) ? jobsRes.data : [];
      const savedCareersData = Array.isArray(careersRes.data) ? careersRes.data : [];
      const assessmentsData = Array.isArray(assessmentsRes.data) ? assessmentsRes.data : [];
      const plansData = Array.isArray(plansRes.data) ? plansRes.data : [];
      
      setResumes(allResumes);
      setCoverLetters(coverLettersData);
      setSavedJobs(savedJobsData);
      setSavedCareers(savedCareersData);
      setAssessments(assessmentsData);
      setDashboardData({
        career_plans: plansData,
        career_plans_count: plansData.length
      });
      
      dashboardCache.data = {
        resumes: allResumes,
        coverLetters: coverLettersData,
        savedJobs: savedJobsData,
        savedCareers: savedCareersData,
        assessments: assessmentsData,
        dashboardData: { career_plans: plansData, career_plans_count: plansData.length }
      };
      dashboardCache.timestamp = Date.now();
    } catch (error) {
      console.error('Fallback fetch also failed:', error);
    }
  };

  const deleteSavedCareer = async (id) => {
    try {
      await savedCareersAPI.delete(id);
      setSavedCareers(savedCareers.filter(c => c.id !== id));
      toast.success('Career removed from saved list');
    } catch (error) {
      toast.error('Failed to remove saved career');
    }
  };

  const performLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    // Trigger pre-logout ad, then perform actual logout
    await triggerLogoutAd(performLogout);
  };

  const deleteResume = async (id, type) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Resume',
      description: 'Are you sure you want to delete this resume? This action cannot be undone.',
      onConfirm: async () => {
        try {
          if (type === 'visual') {
            await visualResumeAPI.delete(id);
          } else {
            await resumeAPI.delete(id);
          }
          setResumes(resumes.filter(r => r.id !== id));
          toast.success('Resume deleted successfully');
        } catch (error) {
          toast.error('Failed to delete resume');
        }
      }
    });
  };

  const deleteCoverLetter = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Cover Letter',
      description: 'Are you sure you want to delete this cover letter? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await coverLetterAPI.delete(id);
          setCoverLetters(coverLetters.filter(l => l.id !== id));
          toast.success('Cover letter deleted successfully');
        } catch (error) {
          toast.error('Failed to delete cover letter');
        }
      }
    });
  };

  const deleteCareerPlan = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Career Plan',
      description: 'Are you sure you want to delete this career plan? This action cannot be undone.',
      onConfirm: async () => {
        try {
          const response = await careerPlannerAPI.deletePlan(id);
          
          if (response.status === 200) {
            setDashboardData({
              ...dashboardData,
              career_plans: dashboardData.career_plans.filter(p => p.id !== id),
              career_plans_count: Math.max(0, dashboardData.career_plans_count - 1)
            });
            toast.success('Career plan deleted successfully');
          }
        } catch (error) {
          console.error('Failed to delete career plan:', error);
          toast.error('Failed to delete career plan');
          fetchData();
        }
      }
    });
  };

  const deleteAssessment = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Assessment Result',
      description: 'Are you sure you want to delete this assessment result? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await assessmentAPI.deleteResult(id);
          setAssessments(assessments.filter(a => a.id !== id));
          toast.success('Assessment result deleted successfully');
        } catch (error) {
          toast.error('Failed to delete assessment result');
        }
      }
    });
  };

  const saveSectionOrderToBackend = async (newOrder) => {
    try {
      await settingsAPI.saveDashboardSectionOrder(newOrder);
    } catch (error) {
      console.error('Failed to save section order to backend:', error);
    }
  };

  const moveSectionUp = (sectionId) => {
    const currentIndex = sectionOrder.indexOf(sectionId);
    if (currentIndex > 0) {
      const newOrder = [...sectionOrder];
      [newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]];
      setSectionOrder(newOrder);
      localStorage.setItem('dashboardSectionOrder', JSON.stringify(newOrder));
      saveSectionOrderToBackend(newOrder);
      toast.success('Section order updated');
    }
  };

  const moveSectionDown = (sectionId) => {
    const currentIndex = sectionOrder.indexOf(sectionId);
    if (currentIndex < sectionOrder.length - 1) {
      const newOrder = [...sectionOrder];
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
      setSectionOrder(newOrder);
      localStorage.setItem('dashboardSectionOrder', JSON.stringify(newOrder));
      saveSectionOrderToBackend(newOrder);
      toast.success('Section order updated');
    }
  };

  // Check if section is visible based on settings
  const isVisible = (sectionKey) => {
    if (!userSettings?.dashboard_visibility) return true;
    return userSettings.dashboard_visibility[sectionKey] !== false;
  };

  // Render dynamic sections based on order
  const renderSections = () => {
    const sectionComponents = {
      purchasedServices: (
        <Suspense key="purchasedServices-suspense" fallback={<SectionSkeleton />}>
          <MyPurchasedServicesSection key="purchasedServices" initialPurchases={userPurchases} />
        </Suspense>
      ),
      resumes: (
        <Suspense key="resumes-suspense" fallback={<SectionSkeleton />}>
          <ResumesSection
            key="resumes"
            resumes={resumes}
            sectionOrder={sectionOrder}
            onMoveSectionUp={moveSectionUp}
            onMoveSectionDown={moveSectionDown}
            onDeleteResume={deleteResume}
            onCreateClick={() => setShowResumeOptionsDialog(true)}
            isVisible={isVisible('resumes')}
          />
        </Suspense>
      ),
      coverLetters: (
        <Suspense key="coverLetters-suspense" fallback={<SectionSkeleton />}>
          <CoverLettersSection
            key="coverLetters"
            coverLetters={coverLetters}
            sectionOrder={sectionOrder}
            onMoveSectionUp={moveSectionUp}
            onMoveSectionDown={moveSectionDown}
            onDeleteCoverLetter={deleteCoverLetter}
            isVisible={isVisible('cover_letters')}
          />
        </Suspense>
      ),
      careerPlans: (
        <Suspense key="careerPlans-suspense" fallback={<SectionSkeleton />}>
          <CareerPlansSection
            key="careerPlans"
            careerPlans={dashboardData.career_plans}
            sectionOrder={sectionOrder}
            onMoveSectionUp={moveSectionUp}
            onMoveSectionDown={moveSectionDown}
            onDeleteCareerPlan={deleteCareerPlan}
            isVisible={isVisible('career_plans')}
          />
        </Suspense>
      ),
      assessments: (
        <Suspense key="assessments-suspense" fallback={<SectionSkeleton />}>
          <AssessmentsSection
            key="assessments"
            assessments={assessments}
            sectionOrder={sectionOrder}
            onMoveSectionUp={moveSectionUp}
            onMoveSectionDown={moveSectionDown}
            onDeleteAssessment={deleteAssessment}
            isVisible={isVisible('career_assessments')}
          />
        </Suspense>
      )
    };

    return sectionOrder.map(sectionId => sectionComponents[sectionId]).filter(Boolean);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50">
        {/* Navigation skeleton */}
        <div className="px-6 py-4 bg-white/80 backdrop-blur-md border-b border-gray-200">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="h-12 w-32 bg-gray-200 rounded-lg animate-pulse" />
            </div>
            <div className="flex items-center gap-3">
              <div className="h-8 w-24 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
        
        {/* Main content skeleton */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <DashboardSkeleton />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50">
      <SkipNav />
      
      {/* Universal Logged-In Header */}
      <LoggedInHeader user={user} onLogout={handleLogout} />
      
      {/* Dashboard-Specific Navigation (role-based tools) */}
      <DashboardNav 
        user={user}
        hasRole={hasRole}
        isAdmin={isSuperAdmin()}
        hasOrgMembership={hasOrgMembership}
        onLogout={handleLogout}
        onPersonalizePath={handlePersonalizePath}
      />

      {/* Founder Countdown Banner */}
      {isFounder && showCountdownBanner && !bannerDismissed && (
        <FounderCountdownBanner
          bannerType={countdownBannerType}
          daysRemaining={founderDaysRemaining}
          message={countdownMessage}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}

      {/* Organization Premium Access Banner */}
      <OrgPremiumAccessBanner user={user} />
      
      {/* Premium Expired Modal (shows once per session when premium expires) */}
      <PremiumExpiredModal user={user} />

      {/* Partner Onboarding Modal (shows for new partner role assignments) */}
      <PartnerOnboarding user={user} />

      {/* Main Content */}
      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 lg:py-12 lg:mt-0 mt-14 pb-20 sm:pb-8">
        {/* Affiliate Agreement Banner */}
        <AffiliateBanner 
          affiliateStatus={affiliateStatus}
          onDismiss={() => setAffiliateStatus(null)}
        />

        {/* Post-Step Encouragement (shows after first action completed) */}
        <PostStepEncouragement />

        {/* Founder Trial Badge (shows for Founder Circle members) */}
        <FounderTrialBadge />

        {/* Admin Widget: Founders Expiring Soon (only for super_admin) */}
        {isSuperAdmin() && (
          <FoundersExpiringSoonWidget className="mb-6" />
        )}

        {/* GPS Start Here Card (Personalized or Fallback) */}
        <GPSStartHereCard 
          onPersonalizePath={handlePersonalizePath} 
          onStartOnboarding={() => setShowGPSOnboarding(true)}
        />

        {/* Legacy Recommended Next Step - Hidden if GPS is active */}
        {!gpsData?.completed && <RecommendedAction />}

        {/* Welcome Section */}
        <header className="mb-8 sm:mb-12">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-2 sm:mb-4">Your Career Hub</h1>
              <p className="text-base sm:text-lg text-gray-600">Everything you need to land your dream job</p>
            </div>
            {/* Account Type Badge */}
            {user && (
              <div className="flex items-center gap-2">
                <AccountTypeBadge user={user} />
              </div>
            )}
          </div>
        </header>

        {/* Quick Actions */}
        <QuickActionsGrid 
          user={user}
          userSettings={userSettings}
          setUserSettings={setUserSettings}
          hasOrgMembership={hasOrgMembership}
          affiliateStatus={affiliateStatus}
          isAdmin={isSuperAdmin()}
          accessStatus={accessStatus}
          progressBadges={progressBadges}
          gedStatus={gedStatus}
          gpsData={gpsDataFromApi}
        />

        {/* Coming Soon Section (Admin Preview Only) */}
        <ComingSoonSection user={user} />

        {/* Stats Grid */}
        <StatsGrid 
          resumes={resumes}
          coverLetters={coverLetters}
          savedJobs={savedJobs}
          careerPlansCount={dashboardData.career_plans_count}
          assessments={assessments}
        />

        {/* My Registered Events - Shows events user has registered for */}
        <div className="mb-8">
          <Suspense fallback={<SectionSkeleton />}>
            <MyRegisteredEvents initialEvents={registeredEvents} />
          </Suspense>
        </div>

        {/* Saved Careers */}
        <Suspense fallback={<SectionSkeleton />}>
          <SavedCareersSection 
            savedCareers={savedCareers}
            onDeleteSavedCareer={deleteSavedCareer}
          />
        </Suspense>

        {/* Dynamic Sections - User Reorderable */}
        {renderSections()}

        {/* Inline Ad - Scroll Trigger */}
        <div className="my-8">
          <AdRenderer 
            zone="inline_content" 
            pagePath="/dashboard" 
            trigger="on_scroll"
            className="rounded-xl"
          />
        </div>

      </main>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        variant="destructive"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Resume Creation Options Dialog */}
      <ResumeOptionsDialog 
        open={showResumeOptionsDialog}
        onOpenChange={setShowResumeOptionsDialog}
      />

      {/* Welcome Tour */}
      {showTour && (
        <WelcomeTour 
          onComplete={() => setShowTour(false)}
          onSkip={() => setShowTour(false)}
        />
      )}

      {/* GPS Onboarding Modal */}
      {showGPSOnboarding && (
        <GPSOnboarding 
          onComplete={handleGPSComplete}
          onSkip={handleGPSSkip}
        />
      )}

      {/* GPS Responses Viewer Modal */}
      <GPSResponsesViewer 
        isOpen={showGPSResponses}
        onClose={() => setShowGPSResponses(false)}
        onReset={handleResetGPS}
      />
      
      {/* Footer */}
      <AppFooter />
    </div>
  );
};

export default Dashboard;
